import { StatePage } from "@/components/state-page";
import { filterKeys, withDefaults } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { requirePermissions } from "@/utils/permissions";
import type { APIOutput } from "@/utils/types";
import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { t } from "i18next";
import { LucideDownload, LucidePlus } from "lucide-react";
import ControlBar from "./-control-bar";
import NewEmployee from "./-new";
import EmployeeTable from "./-table";
import { DEFAULT_EMPLOYEES_PARAMS, EmployeeFiltersSchema } from "./-utils";

export const Route = createFileRoute("/_authenticated/employees/")({
  validateSearch: EmployeeFiltersSchema.extend(withDefaults(EmployeeFiltersSchema, DEFAULT_EMPLOYEES_PARAMS).shape),
  component: Component,
  beforeLoad: requirePermissions("employee:get"),
  loaderDeps: ({ search }) => [search] as const,
  loader: ({ context, deps: [search] }) => {
    void context.queryClient.prefetchQuery(
      orpc.employee.getAll.queryOptions({
        input: { ...DEFAULT_EMPLOYEES_PARAMS, ...search },
      }),
    );
    void context.queryClient.prefetchQuery(
      orpc.employee.count.queryOptions({
        input: search,
      }),
    );
    void context.queryClient.prefetchQuery(orpc.role.getAll.queryOptions());
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="employees" emptyResourceType="employees" />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="employees" emptyResourceType="employees" />,
});

function Component() {
  const search = useSearch({ from: "/_authenticated/employees/" });
  const queryClient = useQueryClient();
  const filters = filterKeys(search, EmployeeFiltersSchema.shape);

  const exportAllMutation = useMutation({
    mutationFn: async () => {
      const [{ pdf, Document }, { EmployeePDFPage }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./-pdf-document"),
      ]);

      const agency = await queryClient.fetchQuery(orpc.agency.get.queryOptions());

      // Fetch all employees with pagination
      const allEmployees: APIOutput["employee"]["getAll"] = [];
      let page = 0;
      const limit = 1000;
      let hasMore = true;

      while (hasMore) {
        const employees = await queryClient.fetchQuery(
          orpc.employee.getAll.queryOptions({
            input: {
              ...DEFAULT_EMPLOYEES_PARAMS,
              ...filters,
              page,
              limit,
              sortBy: filters.sortBy ?? DEFAULT_EMPLOYEES_PARAMS.sortBy,
              sortOrder: filters.sortOrder ?? DEFAULT_EMPLOYEES_PARAMS.sortOrder,
            },
          }),
        );

        allEmployees.push(...employees);

        if (employees.length < limit) {
          hasMore = false;
        } else {
          page++;
        }
      }

      if (allEmployees.length === 0) {
        throw new Error(t("employees.exportPDF.all.empty"));
      }

      // Prepare PDF pages
      const employeePages = allEmployees.map((emp) => (
        <EmployeePDFPage key={emp.id} employee={emp} agency={agency} baseUrl={window.location.origin} />
      ));

      const doc = <Document>{employeePages}</Document>;

      const blob = await pdf(doc).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `employees-${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      notifications.show({
        title: t("success"),
        message: t("employees.exportPDF.all.success.description"),
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: t("error.title"),
        message: error instanceof Error ? error.message : t("employees.exportPDF.all.error.description"),
        color: "red",
      });
    },
  });

  return (
    <div className="gap-md md:gap-lg flex grow flex-col">
      <div className="py-md flex flex-wrap items-center justify-between gap-x-6 gap-y-4 md:justify-start">
        <p className="text-2xl font-semibold md:text-3xl">{t("employees.yourEmployees")}</p>
        <div className="grid grow grid-cols-2 items-center gap-1.5 md:flex">
          <Button
            variant="default"
            leftSection={<LucideDownload className="size-4" />}
            onClick={() => exportAllMutation.mutate()}
            loading={exportAllMutation.isPending}
            disabled={CONSTANTS.DEMO}
          >
            {t("export")}
          </Button>
          <NewEmployee
            location="employees"
            target={(onClick) => (
              <Button onClick={onClick} leftSection={<LucidePlus />}>
                {t("employees.new")}
              </Button>
            )}
          />
        </div>
      </div>
      <ControlBar />
      <EmployeeTable />
    </div>
  );
}
